import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { ActionLog, ActionLogsDataI } from 'src/app/core/models/actionLog';
import { ActionLogService } from 'src/app/core/services/action-log.service';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { NodeactionConfirmComponent } from 'src/app/modules/node/modals/nodeaction-confirm/nodeaction-confirm.component';
import * as XLSX from 'xlsx';
import { UserRoleNames } from '../../../../core/models/users';
import { ConfirmDialogComponent } from 'src/app/modules/shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-gateway-logs',
  templateUrl: './gateway-logs.component.html',
  styleUrls: ['./gateway-logs.component.scss']
})
export class GatewayLogsComponent implements OnInit {
  filterText: string = "";
  depName: string;
  currentLoggedUser: string;
  currentUserRole = this.authenticationService.getRole();
  actionLogs: ActionLog[];
  actionLogsToDownload: ActionLog[];
  allActionTypes = [];
  allActionLevels = [];
  allInitiators = [];
  p: number = 1;
  dataAvailable: boolean = true;
  loadActionLogData: Subscription;
  sortDate = 0;
  selectAll = false;
  sortAction = 0;
  sortType = 0;
  sortInitiator = 0;
  defaultValueOfEntry = this.env.envVariables.logsPerRequestCount;
  entries = this.defaultValueOfEntry;
  logNumberEntries: number[] = [];
  totalLogsCount: number;

  // variables to store current filter
  actionFilterValue = 'all';
  actionLevelFilterValue: number = 10; // 10 represents all
  initiatorFilterValue = 'all';
  selectedActionLogs = [];
  showDelete: boolean = true;

  @ViewChild('TABLE') logsTable: ElementRef;

  constructor(private route: ActivatedRoute,
    private navService: NavigationService,
    private spinner: NgxSpinnerService,
    private authenticationService: AuthenticationService,
    private actionLogService: ActionLogService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,private env: EnvironmentService
    
  ) { }


  ngOnInit(): void {
    this.navService.sendParentData('Logs');
    this.navService.sendLeftPanelIndex(1);
    this.navService.breadcrumbDeployment.subscribe(x => {
      this.depName = x ||
        this.route.snapshot.params['deploymentName'] || this.route.parent.snapshot.params['deploymentName']
    }
    );
    this.currentLoggedUser = this.authenticationService.currentUserValue.username;
    this.spinner.show();
    this.showDelete = this.authenticationService.getRole() == UserRoleNames.JobOperator ? false : true;
    this.loadActionLogsData(this.entries)
  }

  loadActionLogsData(entries: number) {
    this.spinner.show();
    this.loadActionLogData = this.actionLogService.getActionLogs("Gateway", this.currentLoggedUser, "all", this.actionFilterValue, this.actionLevelFilterValue, this.initiatorFilterValue, entries)
      .subscribe({
        next: (data: ActionLogsDataI) => {
          this.spinner.hide()
          if (data.actionLogsData == null) {
            if (this.actionLogs) this.actionLogs.length = 0;
            this.dataAvailable = false;
            return;
          }
          this.actionLogs = <ActionLog[]>JSON.parse(JSON.stringify(data.actionLogsData));
          this.dataAvailable = true;
          this.checkIfActionAvailableOnPage()
          this.arrangeActionFilters();
        }, error: (error) => {
          this.spinner.hide();
          this.dataAvailable = false;
        }
      })
  }

  arrangeActionFilters() {
    this.actionLogs.forEach((action: ActionLog) => {
      this.allActionTypes.push(action.action)
      this.allActionLevels.push(action.actionLevel)
      this.allInitiators.push(action.initiator)
    });
    this.allActionTypes = [...new Set(this.allActionTypes)]
    this.allActionLevels = [...new Set(this.allActionLevels)]
    this.allInitiators = [...new Set(this.allInitiators)]
  }

  checkIfActionAvailableOnPage() {
    while (((this.p - 1) * 14) > this.actionLogs.length) {
      this.p--;
    }
  }

  applyFilter() {
    this.spinner.show();
    this.loadActionLogsData(this.entries)
  }

  resetFilter() {
    this.actionFilterValue = 'all';
    this.actionLevelFilterValue = 10;
    this.initiatorFilterValue = 'all';
    this.sortDate = 0
    this.loadActionLogsData(this.entries)
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    if (this.loadActionLogData) this.loadActionLogData.unsubscribe();
    this.spinner.hide();
  }

  sortByTime() {
    if (this.sortDate == 0) {
      this.actionLogs.sort((a, b) => new Date(b.actionTime).getTime() - new Date(a.actionTime).getTime());
    }
    if (this.sortDate == 1) {
      this.actionLogs.sort((b, a) => new Date(b.actionTime).getTime() - new Date(a.actionTime).getTime());
    }
    this.sortDate++
    if (this.sortDate == 2) {
      this.sortDate = 0;
    }
  }

  onSelectAllCheckBox(event) {
    if (event.target.checked) {
      this.selectAll = true
      if (!this.actionLogs) return;
      for (let i = 0; i < this.actionLogs.length; i++) {
        if (!this.selectedActionLogs.includes(this.actionLogs[i].actionLogId)) {
          this.selectedActionLogs.push(this.actionLogs[i].actionLogId)
        }
      }
    } else {
      this.selectedActionLogs = []
      this.selectAll = false
    }
  }

  onCheckboxSelect(event, actionTime) {
    if (event.target.checked) {
      if (!this.selectedActionLogs.includes(actionTime)) {
        this.selectedActionLogs.push(actionTime)
      }
      if (this.selectedActionLogs.length == this.actionLogs.length) {
        this.selectAll = true
      }
    } else {
      let idx = this.selectedActionLogs.indexOf(actionTime)
      this.selectedActionLogs.splice(idx, 1)
      if (this.selectedActionLogs.length == 0 || this.selectedActionLogs.length != this.actionLogs.length) {
        this.selectAll = false
      }
    }
  }

  deleteActionLog() {
    if (this.selectedActionLogs.length == 0) {
      this.snackBar.open("No action logs selected", "Ok", { duration: 5000 })
      return;
    };
    this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      disableClose: false,
      width: '25%',
      data: {
        titleMessage: "Delete Confirmation",
        bodyMessage: "Do you want to delete selected action log(s)?",
        cancelText: "Cancel",
        submitText: "Delete"
      }
    }).afterClosed().subscribe(res => {
      if (res) {
        this.actionLogService.deleteActionLogs(this.depName, this.selectedActionLogs)
          .subscribe({
            next: (data: any) => {
              this.selectedActionLogs = [];
              if (this.selectAll) this.selectAll = false;
              this.loadActionLogsData(this.entries);
              this.snackBar.open(data.actionMessage || "Logs have been deleted.", "Ok", {
                duration: 5000
              })
            },
            error: (err) => {
              this.snackBar.open(err.error.actionMessage), "Ok", {
                duration: 5000
              }
            }
          })
      }
    })
  }

  async downloadActionLogs(downloadAllLogs?: boolean) {
    this.snackBar.open("Please wait while we are preparing download", "Ok", { duration: 5000 });

    if (downloadAllLogs) {
      await this.actionLogService.getActionLogs("Gateway", this.currentLoggedUser, "all", this.actionFilterValue, this.actionLevelFilterValue, this.initiatorFilterValue, this.totalLogsCount, true).subscribe({
        next: (data: ActionLogsDataI) => {
          this.spinner.hide();

          if (data.actionLogsData == null) {
            if (this.actionLogs) this.actionLogs.length = 0;
            this.dataAvailable = false;
            return;
          }
          this.actionLogsToDownload = <ActionLog[]>JSON.parse(JSON.stringify(data?.actionLogsData));

          if (!this.actionLogsToDownload || this.actionLogsToDownload.length == 0) {
            this.snackBar.open("No action logs available", "Ok", { duration: 5000 });
            return;
          }
          const wb: XLSX.WorkBook = XLSX.utils.book_new();
          const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.actionLogsToDownload); //converts a DOM TABLE element to a worksheet
          XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

          /* save to file */
          XLSX.writeFile(wb, this.depName + "_GatewatActionLogs.xlsx");
        },
        error: err => {
          this.spinner.hide();
          if (this.actionLogsToDownload) this.actionLogsToDownload.length = 0;
        },
      });
    }
    else {
      if (!this.actionLogs || this.actionLogs.length == 0) {
        this.snackBar.open("No action logs available", "Ok", { duration: 5000 });
        return;
      };
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.actionLogs);//converts a DOM TABLE element to a worksheet
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      /* save to file */
      XLSX.writeFile(wb, this.depName + '_GatewatActionLogs.xlsx');
    }
  }

  sortByAction() {
    if (this.sortAction == 0) {
      this.actionLogs.sort((a, b) => 0 - (a.action.toLowerCase() > b.action.toLowerCase() ? 1 : -1));
    }
    if (this.sortAction == 1) {
      this.actionLogs.sort((a, b) => 0 - (b.action.toLowerCase() > a.action.toLowerCase() ? 1 : -1));
    }
    this.sortAction++
    if (this.sortAction == 2) {
      this.sortAction = 0;
    }
  }

  sortByType() {
    if (this.sortType == 0) {
      this.actionLogs.sort((a, b) => 0 - (a.entityType.toLowerCase() > b.entityType.toLowerCase() ? 1 : -1));
    }
    if (this.sortType == 1) {
      this.actionLogs.sort((a, b) => 0 - (b.entityType.toLowerCase() > a.entityType.toLowerCase() ? 1 : -1));
    }
    this.sortType++
    if (this.sortType == 2) {
      this.sortType = 0;
    }
  }

  sortByInitiator() {
    if (this.sortInitiator == 0) {
      this.actionLogs.sort((a, b) => 0 - (a.initiator.toLowerCase() > b.initiator.toLowerCase() ? 1 : -1));
    }
    if (this.sortInitiator == 1) {
      this.actionLogs.sort((a, b) => 0 - (b.initiator.toLowerCase() > a.initiator.toLowerCase() ? 1 : -1));
    }
    this.sortInitiator++
    if (this.sortInitiator == 2) {
      this.sortInitiator = 0;
    }
  }

  startRange: number = 1;
  loadLogsDataForRange(entry: number) {
    if (this.entries == this.defaultValueOfEntry) {
      this.startRange = 1;
    } else if (this.entries == this.totalLogsCount) {
      this.startRange =
        Number(Math.floor(this.totalLogsCount / this.defaultValueOfEntry) * this.defaultValueOfEntry) + 1;
    } else {
      this.startRange = Number(this.entries - this.defaultValueOfEntry) + 1;
    }
    this.loadActionLogsData(entry);
  }

  getLogsCount() {
    this.loadActionLogData = this.actionLogService.getActionLogsCount("Gateway", this.currentLoggedUser, "all", this.actionFilterValue, this.actionLevelFilterValue, this.initiatorFilterValue)
      .subscribe({
        next: (data: any) => {
          this.logNumberEntries = data?.rangeCounts?.split(",").map(c => Number(c));
          this.totalLogsCount = this.logNumberEntries[this.logNumberEntries.length - 1];
          this.defaultValueOfEntry = this.logNumberEntries[0];
          this.entries = this.logNumberEntries[0];

          if (this.logNumberEntries == this.defaultValueOfEntry) {
            this.startRange = 1;
          } else if (this.entries == this.totalLogsCount) {
            let lastEntry = Number(
              Math.floor(
                this.totalLogsCount / this.defaultValueOfEntry
              ) * this.defaultValueOfEntry
            );
            this.startRange = lastEntry == this.totalLogsCount ? lastEntry - this.defaultValueOfEntry + 1 : lastEntry + 1;
          } else {
            this.startRange =
              Number(this.entries - this.defaultValueOfEntry) +
              1;
          }
          this.loadActionLogsData(this.entries)
        },
        error: (error: any) => {

        }
      });
  }
}
